import React from "react";
import { useQuery } from "@tanstack/react-query";
import { TrendingUp, BarChart3, Store, Tag } from "lucide-react";
import { api, STORE_LABEL, dealTitle } from "../lib/api";

const STORE_COLORS = {
  amazon: "#FF9900",
  aliexpress: "#FF4747",
  ebay: "#86B817",
  altro: "#FBD51C",
};

export default function StatsTab() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: () => api.get("/admin/stats/clicks?days=7").then((r) => r.data),
    refetchOnWindowFocus: false,
  });

  if (isLoading || !data) {
    return (
      <div className="space-y-3">
        <div className="h-12 rounded shimmer" />
        <div className="h-64 rounded shimmer" />
        <div className="h-40 rounded shimmer" />
      </div>
    );
  }

  const maxDay = Math.max(1, ...data.by_day.map((d) => d.clicks));
  const total = data.total_clicks;

  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <h1 className="font-display font-black text-2xl text-white">Statistiche click</h1>
        <p className="text-zinc-500 text-sm">Ultimi 7 giorni · sull&apos;intero sito</p>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Kpi
          icon={<TrendingUp size={16} className="text-[#FBD51C]" />}
          label="Click totali"
          value={total}
        />
        <Kpi
          icon={<BarChart3 size={16} className="text-[#FBD51C]" />}
          label="Media giornaliera"
          value={Math.round(total / 7)}
        />
        <Kpi
          icon={<Store size={16} className="text-[#FBD51C]" />}
          label="Top store"
          value={data.by_store[0]?.name ? STORE_LABEL[data.by_store[0].name] || data.by_store[0].name : "—"}
        />
      </div>

      {/* Daily bar chart */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display font-bold text-white">Click per giorno</h2>
          <span className="font-mono-bdd text-[10px] uppercase tracking-wider text-zinc-500">
            picco: {maxDay}
          </span>
        </div>
        <div data-testid="chart-by-day" className="flex items-end gap-3 h-48">
          {data.by_day.map((d) => {
            const h = (d.clicks / maxDay) * 100;
            const day = new Date(d.date);
            const label = day.toLocaleDateString("it-IT", { weekday: "short" });
            return (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-2">
                <div className="text-xs font-mono-bdd font-bold text-white">
                  {d.clicks}
                </div>
                <div className="relative w-full bg-[#0d162d] rounded-md overflow-hidden" style={{ height: "160px" }}>
                  <div
                    className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#D4B011] via-[#FBD51C] to-[#FFE552] rounded-md transition-all duration-700"
                    style={{ height: `${h}%`, minHeight: d.clicks > 0 ? "4px" : "0" }}
                  />
                </div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono-bdd">
                  {label}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Store breakdown */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
          <h2 className="font-display font-bold text-white mb-4 flex items-center gap-2">
            <Store size={16} className="text-[#FBD51C]" /> Per store
          </h2>
          {data.by_store.length === 0 ? (
            <div className="text-zinc-500 text-sm">Nessun dato</div>
          ) : (
            <div className="space-y-3">
              {data.by_store.map((s) => {
                const pct = total ? (s.clicks / total) * 100 : 0;
                return (
                  <div key={s.name}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-white font-bold">
                        {STORE_LABEL[s.name] || s.name}
                      </span>
                      <span className="font-mono-bdd text-zinc-400">
                        {s.clicks} · {pct.toFixed(0)}%
                      </span>
                    </div>
                    <div className="h-2 bg-[#0d162d] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{
                          width: `${pct}%`,
                          background: STORE_COLORS[s.name] || "#FBD51C",
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
          <h2 className="font-display font-bold text-white mb-4 flex items-center gap-2">
            <Tag size={16} className="text-[#FBD51C]" /> Per categoria
          </h2>
          {data.by_category.length === 0 ? (
            <div className="text-zinc-500 text-sm">Nessun dato</div>
          ) : (
            <div className="space-y-3">
              {data.by_category.slice(0, 6).map((c) => {
                const pct = total ? (c.clicks / total) * 100 : 0;
                return (
                  <div key={c.name}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-white font-bold capitalize">
                        {c.name}
                      </span>
                      <span className="font-mono-bdd text-zinc-400">
                        {c.clicks} · {pct.toFixed(0)}%
                      </span>
                    </div>
                    <div className="h-2 bg-[#0d162d] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-[#FBD51C] to-[#FFE552] transition-all duration-700"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Top deals */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
        <h2 className="font-display font-bold text-white mb-4">Top 5 offerte (periodo)</h2>
        {data.top_deals.length === 0 ? (
          <div className="text-zinc-500 text-sm">Nessun click negli ultimi 7 giorni</div>
        ) : (
          <div className="space-y-2" data-testid="top-deals">
            {data.top_deals.map((d, i) => (
              <div
                key={d.id}
                className="flex items-center gap-3 py-2 border-b border-zinc-800 last:border-b-0"
              >
                <div className="w-7 h-7 rounded-full bg-[#FBD51C]/15 text-[#FBD51C] flex items-center justify-center font-mono-bdd font-bold text-xs">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-white truncate">{dealTitle(d)}</div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                    {STORE_LABEL[d.store] || d.store}
                  </div>
                </div>
                <div className="font-mono-bdd text-sm font-bold text-[#FBD51C]">
                  {d.clicks_period} click
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ icon, label, value }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
      <div className="flex items-center gap-2 text-[10px] font-mono-bdd uppercase tracking-[0.2em] text-zinc-500">
        {icon} {label}
      </div>
      <div className="font-display font-black text-3xl text-white mt-3 leading-none">
        {value}
      </div>
    </div>
  );
}
